import { Injectable } from '@angular/core';
import firebase from 'firebase';
import { AuthService } from 'src/app/core/auth/auth.service';
import { rejects } from 'assert';
import { FirestoreRulesService } from 'src/app/core/firestore-rules/firestore-rules.service';
import { LocalStorageService } from 'src/app/core/local-storage/local-storage.service';
import { LocalStorageKeys } from 'src/app/core/local-storage/local-storage.enum';
import { combineLatest, from, Observable, Subject } from 'rxjs';
import { IUser, IUserMetadata } from '../interfaces/user.interface';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { UserAdminVerificationStatusEnum, UserValuesData } from '../interfaces/user-values';
import { LocaleEnum } from 'src/app/submit-lecture/lecture.values';
import { ILecture, ILectureFeedback } from 'src/app/views/pages/lectures/lecture.interface';
import { UserScoreService } from './user-score.service';
import { ToastrService } from 'ngx-toastr';

const ALL_USERS_LAST_LECTURE_CHANGED_UPDATE_DATE = `ALL_USERS_LAST_LECTURE_CHANGED_UPDATE_DATE_${environment.production ? 'prod' : 'dev'}`;
export const MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING = 2;

@Injectable({
  providedIn: 'root'
})
export class UserService {

  photoChanged = new Subject();

  constructor(private readonly authService: AuthService,
              private readonly firestoreRulesService: FirestoreRulesService,
              private localStorageService: LocalStorageService,
              private readonly translate: TranslateService,
              private readonly toastr: ToastrService,
              private userScoreService: UserScoreService) {
  }

  getUserInfo(): any {
    return firebase.auth().currentUser;
  }

  getCurrentUserDetails(): Promise<any> {
    return new Promise<any>(async (resolve, reject) => {

      const user = await this.setCurrentUserDetails();
      resolve(user);
    });
  }

  setCurrentUserDetails(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.authService.getCurrentUser()
        .then(async (user) => {

          // enrich with metadata
          const userMeta = await this.getUserMetadata(user);
          user.metadata = userMeta;

          this.localStorageService.write(LocalStorageKeys.currentUser, JSON.stringify(user));
          resolve(user);

        }).catch((err) => {
          reject(err);
        });
    });
  }

  updateUserMetadataByDocId(docId: string, metadata): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const current = firebase.auth().currentUser;
      this.setUserMetadataById(metadata).then(res => {
        if (current.uid === metadata.docId) { // self profile
          this.localStorageService.write(LocalStorageKeys.userMetadata, metadata);
          this.authService.setUserDisplayName(metadata.firstName, metadata.lastName).then((user) => {
            resolve(user);
          });
        } else {
          resolve(metadata);
        }
      }).catch(err => {
        reject(err);
      });
    });
  }

  deleteReminderByUser(email: string): void {
    this.firestoreRulesService.deleteReminderByUser(email);
  }

  updateUserMetadata(metadata): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        this.setUserMetadata(metadata);
        this.authService.setUserDisplayName(metadata.firstName, metadata.lastName).then((user) => {
          resolve(user);
        });
      } catch (err) {
        reject(err);
      }
    });
  }

  setUserMetadataById(metadata): Promise<any> {
    return this.firestoreRulesService.updateUserMetaData(metadata.docId, metadata);
  }

  setUserMetadata(metadata): void {
    this.firestoreRulesService.updateUserMetaData(metadata.docId, metadata);
    this.localStorageService.write(LocalStorageKeys.userMetadata, metadata);
  }

  getUserById(userId: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const data = this.firestoreRulesService.getUserMetadata(userId);
      data.subscribe((meta) => {
        resolve(meta);
      }, err => {
        console.log(err);
        reject(err);
      });
    });
  }

  getUserMetadata(user: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      const metadata = this.localStorageService.read(LocalStorageKeys.userMetadata) as any;
      const status = metadata?.adminVerificationStatus;
      if ((metadata && status && status === UserAdminVerificationStatusEnum.APPROVED) && (user.email === metadata.email) ){
        resolve(metadata);
      } else {
        const data = this.firestoreRulesService.getUserMetadata(user.uid);
        data.subscribe((meta) => {
          this.localStorageService.write(LocalStorageKeys.userMetadata, meta);
          resolve(meta);
        }, err => {
          console.log(err);
          reject(err);
        });
      }
    });
  }

  getUserPreferences(user: any): any {
    return new Promise<any>((resolve, reject) => {
      const data = this.firestoreRulesService.getUserPreferences(user.uid);
      data.subscribe((metadata) => {
        resolve(metadata);
      }, err => {
        console.log(err);
        rejects(err);
      });
    });
  }

  getAllUsersByFilter(query: Function = undefined, forceCache = false): Promise<IUserMetadata[]> {
    let lastChangedDocs;

    return new Promise<any>(async (resolve, reject) => {
        try {
          const lastUserUpdateFromLocalStorage = localStorage.getItem(ALL_USERS_LAST_LECTURE_CHANGED_UPDATE_DATE);
          let docs = await this.firestoreRulesService.getAllUsers(true, query);
          // check if any user was updated since last cached
          if (docs && docs.length) {
            lastChangedDocs = await this.firestoreRulesService.getLastChangedUser();
          }
          // if data was not fetched from cache or changes exist on fb users data then get data from fb
          if (this.isRemoteDataFetching(forceCache, docs, lastUserUpdateFromLocalStorage, lastChangedDocs) ||
            docs?.length <= MIN_CACHE_ROWS_2_TRIGGER_REMOTE_FETCHING) {
            docs = await this.firestoreRulesService.getAllUsers(false, query);
            if (lastChangedDocs && lastChangedDocs.length) {
              const changedDate = this.convertToDate(lastChangedDocs[0]?.updated_at);
              if (changedDate) {
                localStorage.setItem(ALL_USERS_LAST_LECTURE_CHANGED_UPDATE_DATE, changedDate.toString());
              }
            }
          }
          resolve(docs);
        } catch (error) {
          console.log(error);
          reject(error);
        }
      });
  }

  getAllUsers(query: Function = undefined, forceCache = false): Observable<IUser[]> {
    const allUsers$ = from(this.getAllUsersByFilter(query, forceCache) as Promise<any[]>);

    return combineLatest([allUsers$]).pipe(
      switchMap(([users]) => (
        combineLatest(users.map(x => this.translate.stream([x.adminVerificationStatus]))).pipe(
          map(t => ({
            translated: t.map(x => Object.entries(x)),
            users,
          }))
        )
      )),
      map(x => x.users.map((l, i) => {
        l.metadata.translated = {
          adminVerificationStatus: x.translated[i][0][1]
        };
        return l;
      }),
      )
    );
  }

  updateUserPreferences(updatedPrefs): Promise<any> {
    return this.firestoreRulesService.updateUserPreferences(updatedPrefs.docId,
      {
        locations: updatedPrefs.locations ? updatedPrefs.locations : [],
        age_groups: updatedPrefs.age_groups ? updatedPrefs.age_groups : []
      });
  }

  updateUserStatus(userId: string, adminVerificationStatus: UserAdminVerificationStatusEnum): Promise<any> {
    return this.firestoreRulesService.updateUserMetaData(userId,
      { adminVerificationStatus });
  }

  isAdmin(): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        let isAdmin = this.localStorageService.readWithExpiry(LocalStorageKeys.currentRole);
        if (isAdmin !== null && typeof isAdmin !== 'undefined' ) {
          resolve(isAdmin as boolean);
        } else {
          const user = await this.authService.getCurrentUser();
          if (!user) {
            resolve(false);
          }
          isAdmin = await this.firestoreRulesService.getisUserAdmin(user?.uid);
          this.localStorageService.writeWithExpiry(LocalStorageKeys.currentRole, isAdmin, 300000); // 5m expiry time
          resolve(isAdmin as boolean);
        }
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  isAdminByUid(uid: string): Promise<boolean> {
    return new Promise<boolean>(async (resolve, reject) => {
      try {
        const isAdmin = await this.firestoreRulesService.isUserAdmin(uid);
        resolve(isAdmin);
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  deleteUserMetadata(uid: string): Promise<void> {
    return this.firestoreRulesService.deleteUserMetadata(uid);
  }

  deleteUser(uid: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const isAdmin = await this.firestoreRulesService.getisUserAdmin(uid);
        if (isAdmin) {
          await this.firestoreRulesService.removeUserFromAdmin(uid);
        } else {
          await this.firestoreRulesService.removeUserFromRoles(uid);
        }
        await this.deleteUserMetadata(uid);

        resolve();
      } catch (err) {
        console.error(err);
        reject(err);
      }
    });
  }

  getUserAdminVerificationStatus(): Promise<UserAdminVerificationStatusEnum> {
    return new Promise<any>(async (resolve, reject) => {

      this.authService.getCurrentUser()
        .then(async (user) => {
          this.getUserMetadata(user).then((userMeta) => {
            if (userMeta) {
              const status = userMeta.adminVerificationStatus ?
                userMeta.adminVerificationStatus : UserAdminVerificationStatusEnum.PENDING;
              resolve(status);
            } else {
              resolve(UserAdminVerificationStatusEnum.PENDING);
            }
          });
        }).catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  }

  getAdminVerificationDisplayName(user: IUser, lang: string): string {
    const filtered = UserValuesData.userAdminStatuses.filter(status => {
      return (user.adminVerificationStatus === status.id);
    });

    if (!filtered || filtered.length === 0) {
      return user.adminVerificationStatus;
    }

    return (lang === LocaleEnum.HE) ? filtered[0].nameHE : filtered[0].nameEN;
  }

  convertToDate(d: any): Date {
    if (!d) {
      return;
    }
    if (d instanceof Date) {
      return d;
    }

    const seconds = (d as any).seconds;
    const date = new Date(seconds * 1000);
    return date;
  }

  private isLastUserUpdateGreaterThanLocalStorageDate(lastChangedDocs: ILecture[], lastUserUpdateFromLocalStorage: string): boolean {
    const lastUserUpdateDate = new Date(lastUserUpdateFromLocalStorage);
    const lastDocUpdateDate = this.convertToDate(lastChangedDocs[0].updated_at);
    return lastDocUpdateDate > lastUserUpdateDate;
  }

  private isRemoteDataFetching =
  (forceCache: boolean, docs: any[], lastUserUpdateFromLocalStorage: string, lastChangedDocs: ILecture[]): boolean => {
    let result;
    result  = !forceCache && (!docs || docs.length === 0 || !lastUserUpdateFromLocalStorage || (lastChangedDocs && lastChangedDocs.length
                && this.isLastUserUpdateGreaterThanLocalStorageDate(lastChangedDocs, lastUserUpdateFromLocalStorage)));
    return result;
  }

  updateProgress(feedback: ILectureFeedback): Promise<any> {
    return new Promise<any>((resolve, reject) => {
        this.getCurrentUserDetails().then((user: IUser) => {
            const metadata = user.metadata;

            if (!metadata.progress) {
                metadata.progress = {numOfCompleted: 0, numOfParticipants: 0, totalScore: 0};
            }

            metadata.progress.numOfCompleted += Number(feedback.numberOfLectures);
            metadata.progress.numOfParticipants += Number(feedback.numberOfAttendees);
            metadata.progress.totalScore += Number(this.userScoreService.getClosureScore(feedback));

            this.updateUserMetadataByDocId(user.uid, metadata).then(() => {
                resolve(metadata);
            }).catch((err) => {
                console.error(err);
                this.translate.get('PROFILE.UPDATE_FAIL').subscribe((text: string) => {this.toastr.error(text); });
                resolve(null);
            });
        }).catch((err) => {
            console.log(err);
            this.translate.get('GENERAL.ERROR').subscribe((text: string) => {this.toastr.error(text); });
            resolve(null);
        });
    });

  }

  getPhotoUrlChanged(): Subject<unknown> {
    return this.photoChanged;
  }
}
